import {useCallback, useEffect, useRef, useState} from "react";
import {motion, AnimatePresence} from "framer-motion";
import {Search as SearchIcon, Plus, ShieldCheck, Download, ArrowLeft, Megaphone} from "lucide-react";
import {NAV_ITEMS} from "@/lib/constants";
import {API, apiGet, apiPost} from "@/lib/api";
import {SiteContext} from "@/lib/site";
import axios from "axios";
import Logo from "@/components/Logo";
import Home from "@/pages/Home";
import Bazaar from "@/pages/Bazaar";
import Search from "@/pages/Search";
import Circles from "@/pages/Circles";
import Ask from "@/pages/Ask";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Events from "@/pages/Events";
import Panchang from "@/pages/Panchang";
import Culture from "@/pages/Culture";
import Feed from "@/pages/Feed";
import GetFromIndia from "@/pages/GetFromIndia";
import ProductPage from "@/pages/ProductPage";
import Welcome from "@/pages/Welcome";
import {Coming} from "@/components/UI";
import InstallGuide from "@/components/InstallGuide";
import FestivalNudge from "@/components/FestivalNudge";
import NotificationBell from "@/components/NotificationBell";
import RequestModal from "@/pages/modals/Request";
import AuthModal from "@/pages/modals/Auth";
import MemberModal from "@/pages/modals/Member";

const isStandalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
const initialRoute = () => {
  const q = new URLSearchParams(window.location.search);
  return {page: q.get("page") || "home", focus: q.get("focus")};
};

export default function Shell() {
  const [route, setRoute] = useState(initialRoute);
  const [modal, setModalRaw] = useState(null);
  const [toast, setToast] = useState("");
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [listings, setListings] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [wishlist, setWishlist] = useState(() => JSON.parse(localStorage.getItem("samudra-wishlist") || "[]"));
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(() => !isStandalone() && !localStorage.getItem("io-install-dismissed"));
  const [member, setMember] = useState(null);
  const [site, setSite] = useState({content: {}, version: ""});
  const [refreshKey, setRefreshKey] = useState(0);
  const versionRef = useRef("");
  const active = route.page, focus = route.focus;

  const notify = useCallback((m) => { setToast(m); setTimeout(() => setToast(""), 2800); }, []);

  const setActive = useCallback((page, f = null) => {
    if (page === "member" && f) { setMember(f); return; }
    const next = {page, focus: f || null};
    window.history.pushState(next, "", page === "home" ? "/" : `/?page=${page}${f ? `&focus=${encodeURIComponent(f)}` : ""}`);
    setRoute(next); window.scrollTo(0, 0);
  }, []);
  const setModal = (m) => m === "concierge" ? setActive("gfi") : setModalRaw(m);
  const goBack = () => (window.history.state ? window.history.back() : setActive("home"));

  useEffect(() => {
    const onPop = (e) => setRoute(e.state || initialRoute());
    window.addEventListener("popstate", onPop);
    window.history.replaceState(route, "", window.location.pathname + window.location.search);
    return () => window.removeEventListener("popstate", onPop);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const dismissInstall = () => { localStorage.setItem("io-install-dismissed", "1"); setShowInstall(false); };
  const triggerInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const r = await installPrompt.userChoice;
    if (r.outcome === "accepted") notify("Indian Ocean is on your home screen");
    setInstallPrompt(null); dismissInstall();
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, {withCredentials: true}).then(r => setUser(r.data)).catch(() => {}).finally(() => setAuthChecked(true));
  }, []);

  useEffect(() => {
    const load = () => apiGet("/site-content").then(d => {
      if (versionRef.current && d.version !== versionRef.current) { setRefreshKey(k => k + 1); notify("Indian Ocean was updated"); }
      versionRef.current = d.version; setSite(d);
    }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [notify]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/listings`, {withCredentials: true}).then(l => setListings(l.data)).catch(() => {});
    axios.get(`${API}/communities`, {withCredentials: true}).then(c => setCommunities(c.data)).catch(() => {});
  }, [user, refreshKey]);

  useEffect(() => { if (user) apiPost("/track", {page: active, focus}).catch(() => {}); }, [user, active, focus]);
  useEffect(() => { localStorage.setItem("samudra-wishlist", JSON.stringify(wishlist)); }, [wishlist]);

  const toggleWishlist = (id) => setWishlist(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const initials = user?.name ? user.name.split(" ").map(x => x[0]).slice(0, 2).join("").toUpperCase() : "G";
  const requireAuth = (cb) => { if (!user) { setModalRaw("auth"); return; } cb(); };

  if (!authChecked) return <div className="boot-screen" data-testid="boot-screen"><Logo/></div>;
  if (!user) return <SiteContext.Provider value={site.content}><Welcome setUser={setUser} notify={notify}/>{toast && <div className="toast" data-testid="toast-message">{toast}</div>}</SiteContext.Provider>;

  const pageProps = {notify, setModal, user, setUser, listings, setListings, communities, setCommunities, basket: [], toggleBasket: () => {}, wishlist, toggleWishlist, requireAuth, setActive, openMember: setMember, focus};

  const Page = () => {
    switch (active) {
      case "home": return <Home {...pageProps} />;
      case "feed": return <Feed {...pageProps} />;
      case "bazaar": return <Bazaar {...pageProps} />;
      case "product": return <ProductPage {...pageProps} />;
      case "search": return <Search {...pageProps} initialQuery={focus || ""} />;
      case "samaj": return <Circles {...pageProps} />;
      case "ask": return <Ask {...pageProps} />;
      case "gfi": return <GetFromIndia {...pageProps} />;
      case "events": return <Events {...pageProps} />;
      case "panchang": return <Panchang {...pageProps} />;
      case "culture": return <Culture {...pageProps} />;
      case "profile": return <Profile {...pageProps} />;
      case "admin": return <Admin {...pageProps} />;
      default: return <Coming title="Soon" notify={notify} />;
    }
  };

  return (
    <SiteContext.Provider value={site.content}>
    <div className="app-shell">
      <aside className="sidebar">
        <Logo/>
        <div className="member-chip">
          {user.picture ? <img className="avatar" src={user.picture} alt=""/> : <div className="avatar">{initials}</div>}
          <div>
            <strong data-testid="sidebar-user-name">{user.name}</strong>
            <small>{user.is_super_admin ? "Super admin" : user.is_admin ? "Admin" : "Member"}</small>
          </div>
        </div>
        <nav>
          {NAV_ITEMS.filter(([, id]) => id !== "admin" || user.is_admin).map(([label, id, Icon]) => (
            <button key={id} data-testid={`nav-${id}`} className={active === id ? "nav-item active" : "nav-item"} onClick={() => setActive(id)}>
              <Icon size={18}/><span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-bottom">
          {user.is_admin && <a data-testid="open-control-panel" className="install-link" href="/control"><ShieldCheck size={16}/><span>Control panel</span></a>}
          {!isStandalone() && (
            <button data-testid="install-pwa" className="install-link" onClick={() => setShowInstall(true)}>
              <Download size={16}/><span>Install app</span>
            </button>
          )}
        </div>
      </aside>
      <main>
        <header className="topbar">
          <div className="mobile-brand"><Logo compact/></div>
          <div className="top-search">
            <SearchIcon size={17}/>
            <input data-testid="global-search-input" placeholder="Shu shodho cho? Samaj, products, garba..." onKeyDown={e => { if (e.key === "Enter" && e.currentTarget.value.trim()) { setActive("search", e.currentTarget.value.trim()); e.currentTarget.value = ""; } }}/>
          </div>
          <div className="top-actions">
            <button data-testid="mobile-search" className="icon-button mobile-only" onClick={() => setActive("search")}><SearchIcon size={18}/></button>
            <NotificationBell user={user} notify={notify} onOpen={setActive}/>
            <button data-testid="header-create-request" className="coral-button" onClick={() => setActive("gfi")}>
              <Plus size={18}/> <span className="hide-sm">Your Personal Concierge</span>
            </button>
            <button data-testid="header-profile-button" className="mini-avatar" onClick={() => setActive("profile")}>{initials}</button>
          </div>
        </header>
        <div className="page-wrap">
          {site.content.announcement && <div className="announcement" data-testid="announcement-banner"><Megaphone size={15}/> {site.content.announcement}</div>}
          {active !== "home" && (
            <button data-testid="back-button" className="back-button" onClick={goBack}><ArrowLeft size={16}/> Back</button>
          )}
          {active === "home" && <FestivalNudge user={user} setUser={setUser} notify={notify} setActive={setActive}/>}
          <AnimatePresence mode="wait">
            <motion.div key={active + (focus || "") + refreshKey} initial={{opacity: 0, y: 8}} animate={{opacity: 1, y: 0}} exit={{opacity: 0}}>
              {Page()}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
      {modal === "request" && <RequestModal close={() => setModalRaw(null)} notify={notify} setModal={setModal} user={user}/>}
      {modal === "auth" && <AuthModal close={() => setModalRaw(null)} setUser={setUser} notify={notify}/>}
      {member && <MemberModal memberId={member} close={() => setMember(null)} notify={notify} requireAuth={requireAuth} user={user}/>}
      {showInstall && <InstallGuide close={dismissInstall} installPrompt={installPrompt} onInstall={triggerInstall}/>}
      {toast && <div className="toast" data-testid="toast-message">{toast}</div>}
    </div>
    </SiteContext.Provider>
  );
}
